/**
 * HVP UI Manager
 *
 * Manages visual feedback and UI state for variation dropdowns.
 *
 * Features:
 * - Highlighting system (needs-selection, completed states)
 * - Progress indicator (tracks completion percentage)
 * - State caching (avoids redundant DOM updates)
 * - Event-driven updates via scheduler integration
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { UiProgressMethods } from './ui-progress.js';

export class HVPUIManager {
    /**
     * Constructor
     *
     * @param {Object} options - Configuration options
     * @param {boolean} options.debug - Enable debug logging
     * @param {Object} options.scheduler - Update scheduler instance
     */
    constructor(options = {}) {
        this.debug = options.debug || false;
        this.scheduler = options.scheduler || null;

        // Highlight states: 'needs-selection' | 'completed' | 'normal'
        this.highlightState = new Map();

        // Progress tracking
        this.progress = {
            completed: 0,
            total: 0,
            percentage: 0
        };

        // CSS classes
        this.classes = {
            needsSelection: 'hvp-needs-selection',
            completed: 'hvp-completed',
            normal: 'hvp-normal'
        };

        // Statistics
        this.stats = {
            highlightUpdates: 0,
            progressUpdates: 0,
            stateChanges: 0
        };

        // Track initialization state
        this.initialized = false;

        // Bind the event handler to preserve 'this' context
        this._handleUIUpdate = this._handleUIUpdate.bind(this);

        // Set up event listener immediately to prevent race conditions
        // This ensures UI updates can be processed even before initialize() is called
        window.addEventListener('hvp_ui_update', this._handleUIUpdate);
    }

    /**
     * Internal event handler for UI updates
     * @private
     */
    _handleUIUpdate(e) {
        const { type } = e.detail || {};

        switch (type) {
            case 'highlight':
            case 'all':
                this.updateAllHighlighting();
                break;
            case 'progress':
                this.emitProgressUpdate();
                break;
            case 'visibility':
                // Future: handle visibility updates
                break;
        }
    }

    /**
     * Initialize the UI manager
     *
     * Event listeners are already set up in constructor for early availability.
     * This method marks the manager as fully initialized.
     */
    initialize() {
        if (this.initialized) {
            this.log('UI Manager already initialized');
            return;
        }

        this.initialized = true;
        this.log('UI Manager initialized');
    }

    /**
     * Destroy the UI manager and clean up event listeners
     */
    destroy() {
        window.removeEventListener('hvp_ui_update', this._handleUIUpdate);
        this.resetHighlighting();
        this.initialized = false;
        this.log('UI Manager destroyed');
    }

    /**
     * Update highlighting for all dropdowns
     *
     * Determines the state of each dropdown and applies appropriate CSS classes.
     * Only updates dropdowns whose state has changed.
     *
     * @param {NodeList|Array} dropdowns - Optional list of dropdowns to update
     */
    updateAllHighlighting(dropdowns = null) {
        // Get dropdowns if not provided
        const selectElements = dropdowns ||
            document.querySelectorAll('.variations_form select[name^="attribute_"]');

        this.stats.highlightUpdates++;
        let changed = 0;

        selectElements.forEach(select => {
            const selectId = select.name || select.id || select.dataset.attribute_name;
            const currentState = this.determineState(select);
            const previousState = this.highlightState.get(selectId);

            // Only update if state changed
            if (currentState !== previousState) {
                this.applyHighlightState(select, currentState);
                this.highlightState.set(selectId, currentState);
                changed++;
                this.stats.stateChanges++;
            }
        });

        if (changed > 0) {
            this.log(`Highlighting updated: ${changed} dropdowns changed state`);
        }
    }

    /**
     * Determine the highlight state for a dropdown
     *
     * @param {HTMLSelectElement} select - The dropdown element
     * @returns {string} State: 'needs-selection', 'completed', or 'normal'
     */
    determineState(select) {
        const value = select.value;
        const isSingleOption = select.classList.contains('hvp-single-option');
        const isAutoSelected = select.classList.contains('hvp-auto-selected');
        const isDisabled = select.disabled;
        const isEmpty = select.classList.contains('hvp-empty');
        const optionCount = Array.from(select.options).filter(opt => opt.value !== '').length;

        // IMPORTANT: Check single-option FIRST - these should ALWAYS show as 'completed'
        // regardless of disabled state (readonly styling is intentional for single options)
        if (isSingleOption || isAutoSelected) {
            return 'completed';
        }

        // Has a value selected - completed state
        if (value && value !== '') {
            return 'completed';
        }

        // Disabled or empty dropdowns without value are "normal" (no special state)
        if (isDisabled || isEmpty) {
            return 'normal';
        }

        // Has options but no selection - needs attention
        if (optionCount > 0) {
            return 'needs-selection';
        }

        return 'normal';
    }

    /**
     * Apply highlight state CSS classes to dropdown
     *
     * @param {HTMLSelectElement} select - The dropdown element
     * @param {string} state - The state to apply
     */
    applyHighlightState(select, state) {
        // Remove all state classes
        select.classList.remove(
            this.classes.needsSelection,
            this.classes.completed,
            this.classes.normal
        );

        // Add new state class
        switch (state) {
            case 'needs-selection':
                select.classList.add(this.classes.needsSelection);
                break;
            case 'completed':
                select.classList.add(this.classes.completed);
                break;
            case 'normal':
            default:
                select.classList.add(this.classes.normal);
                break;
        }

        this.log(`[Highlight] ${select.name} -> ${state}`);
    }

    /**
     * Reset all highlighting states
     */
    resetHighlighting() {
        this.highlightState.clear();

        const dropdowns = document.querySelectorAll('.variations_form select[name^="attribute_"]');
        dropdowns.forEach(select => {
            select.classList.remove(
                this.classes.needsSelection,
                this.classes.completed,
                this.classes.normal
            );
        });

        this.log('Highlighting reset');
    }

    /**
     * Get statistics
     *
     * @returns {Object}
     */
    getStats() {
        return {
            ...this.stats,
            trackedDropdowns: this.highlightState.size,
            currentProgress: { ...this.progress }
        };
    }

    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = {
            highlightUpdates: 0,
            progressUpdates: 0,
            stateChanges: 0
        };
    }

    /**
     * Log message (debug mode only)
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP UIManager]',
                'background: #ec4899; color: #fff; padding: 2px 6px; border-radius: 3px;',
                ...args
            );
        }
    }
}

// Progress tracking lives in a sibling module (350-line rule).
applyMixin(HVPUIManager.prototype, UiProgressMethods);

export default HVPUIManager;
